import { AWAITING } from './leaveLink'
import { pendingFirst } from './pendingFirst'

interface Request {
  id: string
  employee_id: string
}

/** A person with no `takes_leave` (an older backend) stays listed: an unknown must never hide someone. */
export function listedTeam<P extends { id: string; takes_leave?: boolean }>(
  people: readonly P[],
  awaitingIds: ReadonlySet<string>,
): P[] {
  const listed = people.filter((p) => p.takes_leave !== false || awaitingIds.has(p.id))
  return pendingFirst(listed, (p) => p.id, awaitingIds)
}

const FULL_TIME = 'Full Time'

/** Who a leave-gate override can help: anyone else may only raise Unpaid Leave, which no gate covers. */
export function grantablePeople<P extends { work_type: string; takes_leave?: boolean }>(
  people: readonly P[],
): P[] {
  return people.filter((p) => p.work_type === FULL_TIME && p.takes_leave !== false)
}

/** The pick while it is still listed, else the first listed, so a stale id can never be granted to. */
export function grantTarget(people: readonly { id: string }[], picked: string): string {
  return people.some((p) => p.id === picked) ? picked : (people[0]?.id ?? '')
}

/** Only a spent permission leaves the page: a reserved one is on a live request, and an unknown phase must not vanish. */
export function activeOverrides<O extends { phase: string }>(rows: readonly O[]): O[] {
  return rows.filter((row) => row.phase !== 'SPENT')
}

export function awaitingCounts(awaiting: readonly Request[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const app of awaiting) counts.set(app.employee_id, (counts.get(app.employee_id) ?? 0) + 1)
  return counts
}

export function teamRequests<R extends Request>(awaiting: readonly R[], history: readonly R[]): R[] {
  const seen = new Set(awaiting.map((a) => a.id))
  return [...awaiting, ...history.filter((a) => !seen.has(a.id))]
}

export function forPerson<R extends Request>(rows: readonly R[], personId: string | null): R[] {
  return personId ? rows.filter((r) => r.employee_id === personId) : [...rows]
}

export function toggledPerson(current: string | null, clicked: string): string | null {
  return current === clicked ? null : clicked
}

export function defaultLeaveStatus(scope: string): string {
  return scope === 'awaiting' ? '' : AWAITING
}

export const TEAM_HISTORY_LIMIT = 200

/** True once the team list fills its page: older requests may be cut off. */
export function teamHistoryCapped(rows: readonly unknown[] | undefined): boolean {
  return rows !== undefined && rows.length >= TEAM_HISTORY_LIMIT
}

export const AWAITING_LIMIT = 200

/** The awaiting list is cut when the server counts more than it sent; with no count, only a full page may be cut. */
export function awaitingCut(
  shown: number,
  totalHeader: string | null | undefined,
): { shown: number; total: number | null } | null {
  const total = totalHeader ? Number(totalHeader) : NaN
  if (Number.isInteger(total) && total >= 0) return total > shown ? { shown, total } : null
  return shown >= AWAITING_LIMIT ? { shown, total: null } : null
}
